<template>
    <transition appear name="fade-transition">
        <div :class="classList">
            <v-text-field
                ref="input"
                v-model="amount"
                v-mask="maskValue"
                v-test-id
                :name="name || uuid"
                outlined
                dense
                :rules="validators"
                :label="hideLabel ? null : label"
                background-color="background"
                color="primaryText"
                :hide-details="hideDetails || (!form && _.isEmpty(validators))"
                class="text-right"
                :reverse="reverse"
                :placeholder="hidePlaceholder ? null : 'e.g. £1,234.56'"
                :disabled="disabled"
                :loading="loading"
                :autocomplete="autocomplete ? 'on' : 'off'"
                :error="error"
                :error-messages="errorMessages"
                :readonly="readonly"
                :clearable="!required && clearable"
                @focus="
                    focused = true;
                    $emit('focus', inputValue);
                "
                @blur="
                    focused = false;
                    $emit('blur', inputValue);
                "
                @keyup="$emit('keyup', inputValue)"
                @keydown="$emit('keydown', inputValue)"
                @click="$emit('click', inputValue)"
            >
                <template #prepend>
                    <slot name="prepend" />
                </template>
                <template #prepend-inner>
                    <slot name="prepend-inner" />
                </template>
                <template #append>
                    <slot name="append" />
                    <app-tooltip v-if="info || findSlot('info')" :value="info">
                        <v-icon dense small color="info" style="margin-bottom: 0.1rem">{{
                            getIcon('info', 'fad')
                        }}</v-icon>
                        <template v-if="findSlot('info')" #display>
                            <slot name="info" />
                        </template>
                    </app-tooltip>
                </template>
                <template #append-outer>
                    <slot name="append-outer" />
                </template>
            </v-text-field>
        </div>
    </transition>
</template>

<script>
import Edit from './Edit';
import countries from '@/mixins/countries';

export default {
    name: 'EditMoney',
    extends: Edit,
    mixins: [countries],
    props: {
        reverse: {
            type: Boolean,
            default: false
        },
        presetCurrency: {
            type: String,
            default: null
        },
        autocomplete: {
            type: Boolean,
            default: false
        },
        allowMinus: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        focused: false
    }),
    computed: {
        inputValue: {
            get() {
                let value = { amount: null, currency: null };

                if (this._.isPlainObject(this.value) && 'amount' in this.value) {
                    value.amount = this.value.amount;
                }

                if (this._.isPlainObject(this.value) && 'currency' in this.value) {
                    value.currency = this.value.currency || this.currency.code;
                }

                return value;
            },
            set(v) {
                if (v && typeof v === 'object' && 'amount' in v && v.amount === null) {
                    v = null;
                }

                this.$emit('input', v);
            }
        },
        amountNumber() {
            return this.toDecimal(this.inputValue.amount, {
                precision: this.getOption('decimals', 2),
                callback: money => money.value
            });
        },
        amountString() {
            return this.amountNumber === null ? null : this.amountNumber.toFixed(this.getOption('decimals', 2));
        },
        amount: {
            get() {
                if (this.focused) {
                    return this.amountNumber;
                } else {
                    return this.amountString;
                }
            },
            set(v) {
                this.inputValue = {
                    amount: this.toDecimal(v, {
                        precision: this.getOption('decimals', 2),
                        callback: money => money.value
                    }),
                    currency: this.inputValue.currency || this.currency.code
                };
            }
        },
        currency() {
            let currency = null;

            currency = this.countries[0]?.currency;

            if (this.presetCurrency) {
                currency = this.countries.find(({ currency }) => currency.code === this.presetCurrency)?.currency;
            }

            return currency || { code: 'GBP', name: 'British pound', symbol: '£' };
        },
        maskValue() {
            if (this.mask) {
                return this._.isFunction(this.mask) ? this.mask() : this.mask;
            }

            return this.getMask('number', {
                digits: this.getOption('decimals', 2),
                allowMinus: this.allowMinus || this.getOption('allowMinus', false),
                rightAlign: !this.reverse,
                prefix: this.value?.currency ? this.prefixValue : '',
                suffix: this.suffixValue,
                placeholder: ' '
            });
        },
        prefixValue() {
            let symbol = null;

            if ('currency' in this.inputValue) {
                symbol = this.countries.find(({ currency }) => {
                    return currency.code === this.inputValue.currency;
                })?.currency.symbol;
            }

            if (!symbol) {
                symbol = this.currency.symbol;
            }

            return symbol ? symbol + ' ' : '';
        }
    }
};
</script>

<style lang="scss">
.edit__money {
    min-width: 7rem;
}
</style>
